import React, { useState, useEffect, useCallback } from 'react';
import { Container, Box, Divider, TextField, MenuItem, Typography, Button, Fab, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DataTable from '../components/common/DataTable';
import TableFilters from '../components/common/TableFilters';
import ActionPopover from '../components/common/ActionPopover';
import ConfirmDialog from '../components/common/ConfirmDialog';
import CreateEntityDialog from '../components/common/CreateEntityDialog';
import StatusChip from '../components/common/StatusChip';
import AssignUserSelect from '../components/common/AssignUserSelect';
import DiscussionTextarea from '../components/common/DiscussionTextarea';
import { QUOTATIONS_COLUMNS } from '../config/tableColumnsConfig';
import { QUOTATIONS_FILTERS } from '../config/filterConfig';
import { QUOTATION_STATUS } from '../config/statusConfig';
import quotationsApi from '../services/quotationsApi';
import enquiriesApi from '../services/enquiriesApi';
import { useToast } from '../components/common/ToastProvider';

/**
 * My Quotations Page
 * Displays quotations table with filters, sorting, and actions
 */
const MyQuotations = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ column: '', direction: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  console.log(loading)
  
  // Action popover state
  const [actionAnchor, setActionAnchor] = useState(null);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [updateStatus, setUpdateStatus] = useState('');
  const [assignee, setAssignee] = useState('');
  const [discussion, setDiscussion] = useState('');
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quotationToDelete, setQuotationToDelete] = useState(null);
  
  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [enquiryOptions, setEnquiryOptions] = useState([]);
  
  // Mark Ordered state
  const [markOrderedLoading, setMarkOrderedLoading] = useState(false);

  const { showToast } = useToast();

  // Fetch quotations
  const fetchQuotations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await quotationsApi.list(filters, sort, page, rowsPerPage);
      setQuotations(response.data);
      setTotalRows(response.total);
    } catch (error) {
      console.error('Error fetching quotations:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, sort, page, rowsPerPage]);

  useEffect(() => {
    fetchQuotations();
    // Note: React.StrictMode intentionally double-invokes effects in development
    // This helps catch bugs but may cause duplicate API calls in development mode
  }, [fetchQuotations]);

  // Fetch enquiry options for autocomplete - only once on mount
  useEffect(() => {
    let isMounted = true;
    const fetchEnquiryOptions = async () => {
      // Only fetch if options haven't been loaded yet
      if (enquiryOptions.length > 0) return;
      
      try {
        const response = await enquiriesApi.list({}, {}, 0, 1000);
        if (isMounted) {
          const options = response.data.map(enq => ({
            value: enq.enquiryNumber,
            label: `${enq.enquiryNumber} - ${enq.clientName}`
          }));
          setEnquiryOptions(options);
        }
      } catch (error) {
        console.error('Error fetching enquiry options:', error);
      }
    };
    fetchEnquiryOptions();
    
    return () => {
      isMounted = false;
    };
  }, [enquiryOptions.length]); // Empty dependency array - only run once

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  // Handle sort change
  const handleSortChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setSort({ column: '', direction: '' });
    } else {
      const [column, direction] = value.split('-');
      setSort({ column, direction });
    }
    setPage(0);
  };

  // Handle action button click
  const handleActionClick = (event, row) => {
    setActionAnchor(event.currentTarget);
    setSelectedQuotation(row);
    setUpdateStatus(row.status);
    setAssignee('');
    setDiscussion('');
  };

  // Handle update quotation
  const handleUpdateQuotation = async () => {
    if (selectedQuotation) {
      const updates = {};
      if (updateStatus) updates.status = updateStatus;
      if (assignee) updates.assignee = assignee;
      if (discussion) {
        // In real app, this would append to discussion history
        updates.discussion = discussion;
      }
      
      await quotationsApi.update(selectedQuotation.id, updates);
      setActionAnchor(null);
      setSelectedQuotation(null);
      fetchQuotations();
    }
  };

  // Handle delete
  const handleDeleteClick = (row) => {
    setQuotationToDelete(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (quotationToDelete) {
      await quotationsApi.delete(quotationToDelete.id);
      setDeleteDialogOpen(false);
      setQuotationToDelete(null);
      fetchQuotations();
    }
  };

  // Handle mark ordered
  const handleMarkOrdered = async () => {
    if (!selectedQuotation?.id) {
      showToast('No quotation selected', 'error');
      return;
    }

    setMarkOrderedLoading(true);
    try {
      const response = await quotationsApi.markOrdered(selectedQuotation.id);
      if (response.success) {
        showToast('Order created successfully', 'success');
        setActionAnchor(null);
        setSelectedQuotation(null);
        fetchQuotations();
      } else {
        showToast(response.error || 'Failed to mark ordered', 'error');
      }
    } catch (error) {
      console.error('Error marking quotation ordered:', error);
      showToast('Failed to mark ordered', 'error');
    } finally {
      setMarkOrderedLoading(false);
    }
  };

  // Handle create quotation
  const handleCreateQuotation = async (formData) => {
    try {
      const response = await quotationsApi.create(formData);
      if (response.success) {
        showToast(response.message || 'Quotation created successfully', 'success');
        setCreateDialogOpen(false);
        fetchQuotations();
      } else {
        showToast(response.error || 'Failed to create quotation', 'error');
      }
    } catch (error) {
      showToast('Failed to create quotation', 'error');
    }
  };

  // Create quotation form fields
  const createQuotationFields = [
    { id: 'clientName', label: 'Client Name', type: 'text', required: true },
    { id: 'location', label: 'Location', type: 'text', required: true },
    { 
      id: 'enquiryNumber', 
      label: 'Related Enquiry Number', 
      type: 'autocomplete', 
      required: false
    },
    { id: 'quotationValue', label: 'Quotation Value', type: 'number', required: true },
    { 
      id: 'status', 
      label: 'Quotation Status', 
      type: 'select', 
      required: true,
      options: QUOTATION_STATUS.map(status => ({ value: status, label: status })),
      showStatusChip: true
    },
    { id: 'assigneeId', label: 'Assignee', type: 'assignee', required: false },
    { id: 'discussion', label: 'Initial Discussion', type: 'textarea', required: false }
  ];


  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2, flexWrap: 'wrap', gap: { xs: 1, sm: 1.5 }, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Typography variant="h5" sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' }, fontWeight: 600 }}>
          My Quotations
        </Typography>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, alignItems: 'center', width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}>
          {/* Sort Dropdown */}
          <TextField
            select
            label="Sort By"
            value={sort.column && sort.direction ? `${sort.column}-${sort.direction}` : ''}
            onChange={handleSortChange}
            size="small"
            sx={{ 
              minWidth: { xs: '100%', sm: 180 }, 
              fontSize: '0.875rem',
              flex: { xs: '1 1 auto', sm: '0 0 auto' }
            }}
          >
          <MenuItem value="">No Sort</MenuItem>
          <MenuItem value="quotationNumber-asc">Quotation Number (A-Z)</MenuItem>
          <MenuItem value="quotationNumber-desc">Quotation Number (Z-A)</MenuItem>
          <MenuItem value="lastUpdatedOn-asc">Last Updated (Oldest First)</MenuItem>
          <MenuItem value="lastUpdatedOn-desc">Last Updated (Newest First)</MenuItem>
          <MenuItem value="quotationValue-asc">Value (Low to High)</MenuItem>
          <MenuItem value="quotationValue-desc">Value (High to Low)</MenuItem>
          </TextField>
          {/* Add New Button - Hidden on mobile (shown as FAB) */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              backgroundColor: 'secondary.main',
              color: 'secondary.contrastText',
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: 'secondary.dark',
              },
            }}
          >
            Add New Quotation
          </Button>
        </Box>
      </Box>

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          color="secondary"
          aria-label="add new quotation"
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Filters */}
      <TableFilters
        filters={QUOTATIONS_FILTERS}
        filterValues={filters}
        onFilterChange={handleFilterChange}
        statusOptions={QUOTATION_STATUS}
      />

      {/* Quotations Table */}
      <DataTable
        columns={QUOTATIONS_COLUMNS}
        data={quotations}
        onActionClick={handleActionClick}
        onDeleteClick={handleDeleteClick}
        page={page}
        onPageChange={setPage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={setRowsPerPage}
        totalRows={totalRows}
        mobileHiddenColumns={['initialQuotationDate', 'revisedQuotationDate', 'revisions', 'revise']}
      />

      {/* Update Quotation Popover */}
      <ActionPopover
        open={Boolean(actionAnchor)}
        anchorEl={actionAnchor}
        onClose={() => {
          setActionAnchor(null);
          setSelectedQuotation(null);
        }}
        title="Quotation Actions"
        hideActions={true}
      >
        {/* Mark Ordered Button */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleMarkOrdered}
            disabled={markOrderedLoading}
            sx={{
              backgroundColor: 'success.main',
              color: 'success.contrastText',
              '&:hover': { backgroundColor: 'success.dark' },
              '&:disabled': { backgroundColor: 'action.disabledBackground' },
            }}
          >
            {markOrderedLoading ? 'Processing...' : 'Mark Ordered'}
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Status Update Section */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Update Quotation</Typography>
        <TextField
          select
          label="Quotation Status"
          value={updateStatus}
          onChange={(e) => setUpdateStatus(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        >
          {QUOTATION_STATUS.map((status) => (
            <MenuItem key={status} value={status}>
              <StatusChip status={status} />
            </MenuItem>
          ))}
        </TextField>
        <AssignUserSelect
          value={assignee}
          onChange={setAssignee}
        />
        <DiscussionTextarea
          value={discussion}
          onChange={setDiscussion}
        />

        {/* Footer: Cancel and Update buttons */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'flex-end', gap: { xs: 1, sm: 1.5 }, mt: 2.5, '& > *': { width: { xs: '100%', sm: 'auto' } } }}>
          <Button 
            onClick={() => {
              setActionAnchor(null);
              setSelectedQuotation(null);
            }} 
            variant="outlined"
            size="small"
            sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateQuotation}
            variant="contained"
            color="primary"
            size="small"
            sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
          >
            Update
          </Button>
        </Box>
      </ActionPopover>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Quotation"
        message={`Are you sure you want to delete quotation ${quotationToDelete?.quotationNumber}?`}
      />

      {/* Create Quotation Dialog */}
      <CreateEntityDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateQuotation}
        title="Add New Quotation"
        fields={createQuotationFields}
        relatedEntityOptions={{
          enquiryNumber: enquiryOptions
        }}
        initialValues={{
          clientName: '',
          location: '',
          enquiryNumber: '',
          quotationValue: '',
          status: '',
          assigneeId: '',
          discussion: ''
        }}
      />
    </Container>
  );
};

export default MyQuotations;

